/**
 * Shared primitives — patterns and the context/attribute shapes that every
 * other schema in this package builds on. `spec/SPEC.md` §2–§3 is normative.
 */
import { z } from "zod";
/** Flag keys and segment names share one pattern (SPEC §1.1). */
export const KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;
/** Treatment names: a little more permissive, still ASCII and short. */
export const TREATMENT_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_.-]{0,49}$/;
/** Environment and workspace slugs. */
export const SLUG_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;
/** The treatment returned when a flag is unknown or the SDK is not ready. */
export const CONTROL = "control";
export const flagKey = z
    .string()
    .regex(KEY_PATTERN, "flag key must match ^[a-z0-9][a-z0-9_-]{0,63}$");
export const segmentName = z
    .string()
    .regex(KEY_PATTERN, "segment name must match ^[a-z0-9][a-z0-9_-]{0,63}$");
export const slug = z
    .string()
    .regex(SLUG_PATTERN, "must match ^[a-z0-9][a-z0-9_-]{0,63}$");
export const treatmentName = z
    .string()
    .regex(TREATMENT_PATTERN, "treatment name must match ^[a-zA-Z0-9][a-zA-Z0-9_.-]{0,49}$");
/** The evaluation key (user id, device id, …). */
export const contextKey = z.string().min(1).max(250);
/**
 * Attribute values an app may pass. Lists are string lists only; a number or
 * boolean list must be stringified by the caller (SPEC §3).
 */
export const attributeValue = z.union([
    z.string(),
    z.number().finite(),
    z.boolean(),
    z.array(z.string()),
]);
export const attributes = z.record(z.string().min(1).max(100), attributeValue);
export const evaluationContext = z.object({
    key: contextKey,
    attributes: attributes.optional(),
});
/** Monotonic per-environment version counter (Split's changeNumber). */
export const changeNumber = z.number().int().nonnegative();
/** Unix epoch milliseconds. */
export const epochMillis = z.number().int().nonnegative();
/** RFC 7807 problem document — the only error body the service returns. */
export const problemDetails = z.object({
    type: z.string(),
    title: z.string(),
    status: z.number().int(),
    code: z.string(),
    detail: z.string().optional(),
    instance: z.string().optional(),
    errors: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
    retryAfter: z.number().int().nonnegative().optional(),
});
//# sourceMappingURL=common.js.map